declare global {
  interface Logger {
    info: (v: string, l: object) => void;
  }
}

interface LatencyMetric {
  pathName: string;
  totalTime: number;
}

interface Latency {
  latencyStats: LatencyMetric[];
}

interface LogLatencyMetric {
  domInteractive: number;
  domComplete: number;
  duration: number;
  loadEventEnd: number;
  loadEventStart: number;
  serverLatency: number;
  pathname: string;
  totalTime?: number;
  pageLoadType: 'prefetched' | 'default';
  prefetchedLink: string | null;
  tags: Record<string, unknown>;
}

interface PrefetchedReferrerList {
  [key: string]: string[];
}

function findPathByFullPathName(currentPathName: string, latencyStats: LatencyMetric[]) {
  return latencyStats?.find(el => el.pathName === currentPathName);
}

function isFullPathNameInSession(currentPathName: string, latencyStats: LatencyMetric[]) {
  return !findPathByFullPathName(currentPathName, latencyStats);
}

function filterFirmName(item: string, index: number) {
  if (item !== 'app' && index === 0) {
    return undefined;
  }
  return item;
}

function removeApp(item: string) {
  if (item !== 'app') {
    return item;
  }
  return undefined;
}

function removeUUIDs(item: string) {
  const uuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  if (uuidPattern.test(item)) {
    return undefined;
  }
  return item;
}

function convertPathNamesToKeys(pathname: string) {
  const fullPathName = pathname
    .split('/')
    .slice(1)
    .filter(filterFirmName)
    .filter(removeApp)
    .filter(removeUUIDs)
    .join('-');

  return [fullPathName, fullPathName.replace(/\d/g, '').replace('--', '-')];
}

function getPrefetchPathNameByReferrer() {
  const currentPathName = window.location.pathname;
  let referrer = null;
  try {
    referrer = new URL(document.referrer);
  } catch (e) {
    referrer = { pathname: '' };
  }
  const { pathname } = referrer;
  const prefetchedReferrerList = JSON.parse(
    sessionStorage.getItem('prefetchedReferrerList') || '{}'
  ) as PrefetchedReferrerList;
  const hrefs = prefetchedReferrerList[pathname];

  if (hrefs?.length && hrefs.includes(currentPathName)) {
    const [, convertedPathName] = convertPathNamesToKeys(pathname);
    delete prefetchedReferrerList[pathname];
    sessionStorage.setItem('prefetchedReferrerList', JSON.stringify(prefetchedReferrerList));
    return convertedPathName;
  }

  return null;
}

export default function recordSessionLatency(logger: Logger, isProductionEnv: boolean, tags: Record<string, unknown>) {
  const latency = sessionStorage.getItem('latency');
  let latencyObj: Latency = {
    latencyStats: [],
  };

  try {
    latencyObj = JSON.parse(latency || '') as Latency;
  } catch (e) {
    // fallback to default value
  }

  const { pathname } = window.location;
  const [fullPathName, currentPathName] = convertPathNamesToKeys(pathname);

  const observer = new PerformanceObserver(list => {
    const entry = list.getEntriesByType('navigation')[0];

    const { domInteractive, domComplete, loadEventStart, loadEventEnd, responseEnd, duration } =
      entry as PerformanceNavigationTiming;

    const { latencyStats } = latencyObj;
    const totalTimeFromLogin = findPathByFullPathName('login', latencyStats)?.totalTime ?? 0;

    const latencyMetric: LogLatencyMetric = {
      domInteractive,
      domComplete,
      duration,
      loadEventEnd,
      loadEventStart,
      pathname: currentPathName,
      serverLatency: responseEnd,
      pageLoadType: 'default',
      prefetchedLink: null,
      tags,
    };

    const prefetchPathName = getPrefetchPathNameByReferrer();
    if (prefetchPathName) {
      latencyMetric.pageLoadType = 'prefetched';
      latencyMetric.prefetchedLink = currentPathName;
    }

    if (isFullPathNameInSession(fullPathName, latencyStats)) {
      const totalTime = totalTimeFromLogin + Math.round(loadEventEnd);
      latencyObj.latencyStats.push({
        pathName: fullPathName,
        totalTime,
      });

      sessionStorage.setItem('latency', JSON.stringify(latencyObj));
      latencyMetric.totalTime = totalTime;
    }

    if (isProductionEnv) {
      logger.info('WebLatencyMetric', {
        latency: latencyMetric,
      });
    }
  });

  observer.observe({ type: 'navigation' });
}
